import { useQuery } from '@tanstack/react-query'
import {
  DashboardAlphaReportSummaryCounts,
  PostDashboardAlphaReportSummariesResponse,
} from '@ulysses-inc/harami_api_client'
import { queryKeys } from 'src/features/monitoringDashboardAlpha/api/queryKeys'
import { demoDashboardAlphaApi } from 'src/features/monitoringDashboardAlpha/demo/demoDashboardAlphaApi'
import {
  getDefaultDateRange,
  MonitoringFilters,
} from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import {
  DashboardReportSummaries,
  ReportStatusCounts,
} from 'src/features/monitoringDashboardAlpha/types'
import { stripTimeInfo } from 'src/util/datetime/stripTimeInfo'

/**
 * API の件数内訳（7ステータス + 合計）をフロントの件数型へ写し替える。
 *
 * 生成クライアントと同じフィールド構成のため、そのまま 1:1 で対応付ける。
 * グループ化（実施済み/未実施/期間中/開始前）は表示側の selector(toReportStatusGroups) で行う。
 */
const toCounts = (
  counts: DashboardAlphaReportSummaryCounts,
): ReportStatusCounts => ({
  implemented: counts.implemented,
  implementedAfterDeadline: counts.implementedAfterDeadline,
  recording: counts.recording,
  recordingOverdue: counts.recordingOverdue,
  notImplementedYet: counts.notImplementedYet,
  duringPeriod: counts.duringPeriod,
  notStarted: counts.notStarted,
  total: counts.total,
})

/**
 * API のレスポンスをフロントエンドで定めた形式に変換する
 *
 * - 現場行は placeNodeUUID / counts のネストを平坦化する
 * - 並び順（実施率の昇順）・ページネーションはサーバーサイドで確定済みのため、
 *   フロントでは再ソートしない
 */
const adaptResponse = (
  raw: PostDashboardAlphaReportSummariesResponse,
): DashboardReportSummaries => ({
  summary: toCounts(raw.summary),
  placeReportSummaries: raw.placeReportSummaries.map(place => ({
    placeNodeUuid: place.placeNodeUUID,
    placeName: place.placeName,
    ...toCounts(place.counts),
  })),
  count: raw.count,
})

/**
 * モニタリングダッシュボード（アルファ版 L1）の集計データを取得する
 *
 * セールスデモ（HRMSHI-250）の間は API（POST /dashboardAlpha/reportSummaries, harami_api_server #4833）を呼ばず、
 * demo/ で生成したダミーデータを返す。リクエストの組み立てと adaptResponse は API 呼び出し時と同じ。
 */
export const useQueryDashboardReportSummaries = (
  filters: MonitoringFilters,
) => {
  return useQuery<DashboardReportSummaries>({
    queryFn: async () => {
      // 期間が指定されていればその期間を、指定されていなければ既定期間（今日）を取得対象とする
      const [from, through] = filters.dateRange ?? getDefaultDateRange()
      const startDate = stripTimeInfo(new Date(from))
      const endDate = stripTimeInfo(new Date(through))

      const rawResponse =
        await demoDashboardAlphaApi.postDashboardAlphaReportSummaries({
          startDate,
          endDate,
          placeNodeIds:
            filters.placeNodeUuids.length > 0
              ? filters.placeNodeUuids
              : undefined,
          offset: filters.offset,
          limit: filters.limit,
        })

      return adaptResponse(rawResponse)
    },
    queryKey: queryKeys.dashboardReportSummaries(filters),
  })
}
